import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, FileSpreadsheet, X, ChevronRight } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import type { Customer } from '@billflow/shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

// ─── Customer Modal ────────────────────────────────────────────────────────────

interface CustomerModalProps {
  mode: 'create' | 'edit';
  customer?: Customer;
  onClose: () => void;
  onSaved: () => void;
}

function CustomerModal({ mode, customer, onClose, onSaved }: CustomerModalProps) {
  const [code, setCode] = useState(customer?.code ?? '');
  const [name, setName] = useState(customer?.name ?? '');
  const [contactPerson, setContactPerson] = useState(customer?.contact_person ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [email, setEmail] = useState(customer?.email ?? '');
  const [address, setAddress] = useState(customer?.address ?? '');
  // Credit limit stored in DB as cents; input in dollars
  const [creditLimitDollars, setCreditLimitDollars] = useState(
    customer ? String(customer.credit_limit / 100) : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const creditCents = creditLimitDollars ? Math.round(parseFloat(creditLimitDollars) * 100) : 0;

      const body = {
        code: code.trim() || undefined,
        name: name.trim(),
        // Send '' (not undefined) so clearing an optional field persists — JSON.stringify
        // drops undefined keys, which on PATCH means "leave unchanged" (can't remove a value).
        contact_person: contactPerson.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        credit_limit: creditCents,
      };

      if (mode === 'create') {
        await apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Customer created');
      } else {
        await apiFetch(`/api/customers/${customer!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Customer updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">
            {mode === 'create' ? 'New Customer' : 'Edit Customer'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Row: Code + Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-code">Code</Label>
              <Input
                id="c-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank"
                disabled={loading}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="c-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Contact Person */}
          <div className="space-y-1.5">
            <Label htmlFor="c-contact">Contact Person</Label>
            <Input
              id="c-contact"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Row: Phone + Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-phone">Phone</Label>
              <Input
                id="c-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input
                id="c-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <Label htmlFor="c-address">Address</Label>
            <textarea
              id="c-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={loading}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          {/* Credit Limit */}
          <div className="space-y-1.5">
            <Label htmlFor="c-credit">Credit Limit ($)</Label>
            <Input
              id="c-credit"
              type="number"
              min="0"
              step="0.01"
              value={creditLimitDollars}
              onChange={(e) => setCreditLimitDollars(e.target.value)}
              placeholder="0.00"
              disabled={loading}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : mode === 'create' ? 'Create Customer' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Import Results Modal ──────────────────────────────────────────────────────

interface ImportResultsModalProps {
  result: ImportResult;
  onClose: () => void;
}

function ImportResultsModal({ result, onClose }: ImportResultsModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Import Results</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {result.imported > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
              <span className="font-semibold">✓ {result.imported}</span>
              <span>customer{result.imported !== 1 ? 's' : ''} imported successfully</span>
            </div>
          )}
          {result.skipped > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              <span className="font-semibold">{result.skipped}</span>
              <span>skipped — codes already exist</span>
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3">
              <p className="text-sm font-semibold text-red-800 mb-2">
                {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} had errors:
              </p>
              <ul className="space-y-1">
                {result.errors.map((e, idx) => (
                  <li key={`${e.row}-${idx}`} className="text-xs text-red-700">
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <a
              href="/api/customers/import/template"
              download
              className="text-sm text-primary underline-offset-4 hover:underline"
            >
              Download template
            </a>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  customer: Customer;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ customer, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Customer</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{customer.name}</span>? This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

type StatusFilter = 'active' | 'inactive' | 'all';

const statusTabs: { label: string; value: StatusFilter }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

export default function CustomersPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('active');
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | undefined>(undefined);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    rows: customers,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<Customer>({
    endpoint: '/api/customers',
    entityKey: 'customers',
    defaultSort: 'created',
    defaultDir: 'desc',
    extraParams: { status: status !== 'all' ? status : undefined },
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  }

  function openCreate() {
    setEditingCustomer(undefined);
    setModalMode('create');
  }

  function openEdit(c: Customer) {
    setEditingCustomer(c);
    setModalMode('edit');
  }

  async function handleDelete() {
    if (!deletingCustomer) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/customers/${deletingCustomer.id}`, { method: 'DELETE' });
      toast.success('Customer deleted');
      setDeletingCustomer(undefined);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete customer';
      toast.error(msg);
      setDeletingCustomer(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so re-selecting same file triggers onChange
    e.target.value = '';

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/customers/import', {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // Do NOT set Content-Type — browser sets multipart boundary automatically
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      const result = (await res.json()) as { data: ImportResult };
      setImportResult(result.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/customers"
            params={{
              q: q || undefined,
              status: status !== 'all' ? status : undefined,
            }}
          />
          {isAdmin && (
            <>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                {importing ? 'Importing…' : 'Import Excel'}
              </Button>
            </>
          )}
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Customer
          </Button>
        </div>
      </div>

      {/* Filters — one line: search · status pills (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, code, phone…"
            className="pl-9 h-9"
          />
        </div>

        {/* Status pills */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Code"
                sortKey="code"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <SortableTh label="Name" sortKey="name" sort={sort} dir={dir} onToggle={toggleSort} />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">
                Phone
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Email
              </th>
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && customers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  No customers found.
                </td>
              </tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link
                    to={`/customers/${c.id}`}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    {c.code ?? '—'}
                  </Link>
                </td>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/customers/${c.id}`} className="text-primary hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                  {c.phone ?? '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {c.email ?? '—'}
                </td>
                <td className="px-4 py-3">
                  {c.status === 'active' ? (
                    <Badge variant="green">Active</Badge>
                  ) : (
                    <Badge variant="red">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link to={`/customers/${c.id}`}>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                        View
                        <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </Link>
                    {isAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => openEdit(c)}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                          onClick={() => setDeletingCustomer(c)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modals */}
      {modalMode && (
        <CustomerModal
          mode={modalMode}
          customer={editingCustomer}
          onClose={() => {
            setModalMode(null);
            setEditingCustomer(undefined);
          }}
          onSaved={refetch}
        />
      )}

      {deletingCustomer && (
        <DeleteDialog
          customer={deletingCustomer}
          onCancel={() => setDeletingCustomer(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {importResult && (
        <ImportResultsModal
          result={importResult}
          onClose={() => {
            setImportResult(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}
